import React, { useState, useEffect, useMemo } from 'react';
import { adminStatsService } from '../services/adminStatsService';
import { STAT_COLUMNS } from '../services/supabase';
import { useSeasonContext } from '../context/SeasonContext';
import TopPerformersCards from './TopPerformersCards';
import '../styles/SeasonStatsLeaderboard.css';

const getSeasonalColumns = (statType) => {
  const columns = STAT_COLUMNS[statType] || [];
  if (statType === 'BLOCKING') {
    return [...columns, 'SAS'];
  }
  return columns;
};

const SeasonStatsLeaderboard = () => {
  const [selectedStatType, setSelectedStatType] = useState('passing');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { selectedSeason, setSelectedSeason, availableSeasons } = useSeasonContext();

  useEffect(() => {
    const fetchStats = async () => {
      if (!selectedSeason) return;

      setLoading(true);
      setError(null);

      try {
        const statTypes = ['PASSING', 'RUSHING', 'RECEIVING', 'BLOCKING', 'DEFENSE', 'KICKING'];
        const statsData = {};

        for (const statType of statTypes) {
          const data = await adminStatsService.getAllSeasonStats(statType, selectedSeason.id);
          statsData[statType.toLowerCase()] = data || [];
        }

        setStats(statsData);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Failed to load statistics');
        setStats({});
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [selectedSeason]);

  const currentStatPlayers = useMemo(() => {
    const currentStats = stats[selectedStatType] || [];
    return Array.isArray(currentStats) ? currentStats : Object.values(currentStats || {});
  }, [stats, selectedStatType]);

  const [sortedPlayers, setSortedPlayers] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    column: 'YRDS',
    ascending: false,
  });

  useEffect(() => {
    let sorted = [...currentStatPlayers];
    
    if (sortConfig.column) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.column];
        const bVal = b[sortConfig.column];

        let comparison = 0;
        if (typeof aVal === 'string') {
          comparison = aVal.localeCompare(bVal);
        } else {
          comparison = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0);
        }

        return sortConfig.ascending ? comparison : -comparison;
      });
    }

    setSortedPlayers(sorted);
  }, [currentStatPlayers, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      ascending: prev.column === column ? !prev.ascending : true,
    }));
  };

  const renderStatValue = (value) => {
    const numValue = parseFloat(value);
    if (numValue > 0) {
      return <span style={{ color: '#4CAF50' }}>{value}</span>;
    } else if (numValue < 0) {
      return <span style={{ color: '#ff6b6b' }}>{value}</span>;
    }
    return value;
  };

  if (loading) {
    return <div className="loading">Loading statistics...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="season-stats-leaderboard">
      <div className="header">
        <div className="league-badge">League Leaders</div>
        <h1>Season Stats Leaderboard</h1>
      </div>

      <div className="season-selector">
        <label htmlFor="season-select">Select Season:</label>
        <select
          id="season-select"
          value={selectedSeason ? String(selectedSeason.id) : ''}
          onChange={(e) => setSelectedSeason(Number(e.target.value))}
        >
          {availableSeasons.map(season => (
            <option key={season.id} value={String(season.id)}>
              {season.name} {season.is_active ? '(Current)' : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="stat-tabs">
        {['Passing', 'Rushing', 'Receiving', 'Blocking', 'Defense', 'Kicking'].map((label, index) => (
          <button
            key={label}
            className={`tab-button ${selectedStatType === label.toLowerCase() ? 'active' : ''}`}
            onClick={() => setSelectedStatType(label.toLowerCase())}
          >
            {label}
          </button>
        ))}
      </div>

      <TopPerformersCards stats={stats} selectedStatType={selectedStatType} blockingMetric="SAS" />

      <div className="leaderboard-table">
        <h2>Full Leaderboard</h2>
        <table>
          <thead>
            <tr>
              {(getSeasonalColumns(selectedStatType.toUpperCase()) || []).map(column => (
                <th key={column} onClick={() => handleSort(column)}>
                  {column} {sortConfig.column === column && (sortConfig.ascending ? '↑' : '↓')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player, index) => (
              <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                {(getSeasonalColumns(selectedStatType.toUpperCase()) || []).map(column => (
                  <td key={column}>{renderStatValue(player[column] || 0)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SeasonStatsLeaderboard;
